import {
  Button,
  Col,
  Input,
  InputNumber,
  LegacyForm,
  Row,
  Select,
  message,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { addComma, isAlphabet } from "@payping/utils/string";
import { ctaId } from "@/assets/googleTrackIds";
import { useAppDispatch, useAppTranslation } from "@/utils";
import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import { setSamanRequestList } from "@payping/redux/actions";
import { ValidSheba } from "@payping/utils/validation";
import React, { useState } from "react";
import { SamanRowType } from "@/pages/DigitalAccount/Saman/Create/SecondStep";
import { maxTransferSaman, minTransferSaman } from "@/pages/DigitalAccount/TransferLimit";
import { BatchPaymentDetailsReasonCodeEnum } from "@payping/api";
import { CuToman } from "@payping/icons";

interface Props {
  form: FormInstance<SamanRowType>;
}
const ManualAdditionSaman: React.FC<Props> = ({ form }) => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async () => {
      const formValues = form.getFieldsValue();
      const transferList = formValues.transfer;

      setIsSubmitClicked(true);
      await form.validateFields();

      setIsSubmitClicked(false);

      const iban = `IR${formValues.iban}`;
      const amount = Number(formValues.amount);
      const { description } = formValues;
      const { reasonCode } = formValues;

      try {
        await dispatch(
          setSamanRequestList({
            operationDetails: [
              ...(transferList || []).map((item: SamanRowType) => {
                const correctIRiban = item.iban?.includes("IR") ? item.iban : `IR${item.iban}`;

                return {
                  amount: item.amount ? Number(item.amount) / 10 : undefined,
                  description: item.description,
                  iban: correctIRiban,
                  reasonCode: item.reasonCode,
                };
              }),
              { amount, iban, description, reasonCode },
            ],
          }),
        );
      } catch (error: any) {
        const contentType = error.headers.get("content-type");
        const errorMsg = contentType.includes("application/json") ? await error.json() : "";
        message.error({ content: errorMsg.error, duration: 8 });
      }

      form.resetFields(["amount", "iban", "description", "reasonCode"]);
    },
    [dispatch, form],
    undefined,
    { useProgress: true },
  );

  const shabaValidation = (value: string) => {
    if (value.includes("IR")) return !ValidSheba(value);
    return !ValidSheba(`IR${value}`);
  };

  return (
    <Row gutter={[8, 16]}>
      <Col xs={24} lg={11}>
        <LegacyForm.Item noStyle dependencies={["iban", "amount"]}>
          {({ getFieldValue }) => (
            <LegacyForm.Item
              className={"tw:mb-0!"}
              name="iban"
              rules={[
                { required: isSubmitClicked && (getFieldValue("iban") || getFieldValue("amount")) },
                {
                  validator(_, value) {
                    if (isSubmitClicked && value && shabaValidation(value)) {
                      return Promise.reject(new Error(t("settlement.error.shabaValidation")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder={t("digitalAccount.samanCreate.shebaInput")}
                addonAfter={"IR"}
                onPressEnter={(event) => {
                  event.preventDefault();
                }}
              />
            </LegacyForm.Item>
          )}
        </LegacyForm.Item>
      </Col>
      <Col xs={24} lg={11}>
        <LegacyForm.Item noStyle dependencies={["iban", "amount"]}>
          {({ getFieldValue }) => (
            <LegacyForm.Item
              className={"tw:mb-0!"}
              name={"amount"}
              rules={[
                { required: isSubmitClicked && (getFieldValue("iban") || getFieldValue("amount")) },
                {
                  validator: (_, value): Promise<void> => {
                    const val = Number(value);
                    return isSubmitClicked && value && (val < minTransferSaman || val > maxTransferSaman)
                      ? Promise.reject(
                          t("settlement.error.amount", { max: addComma(maxTransferSaman), min: minTransferSaman }),
                        )
                      : Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                className="tw:w-full!"
                suffix={<CuToman width={24} height={24} />}
                inputMode="numeric"
                changeOnWheel={false}
                controls={false}
                placeholder={t("digitalAccount.samanCreate.amountInput")}
                onPressEnter={(event) => {
                  event.preventDefault();
                }}
              />
            </LegacyForm.Item>
          )}
        </LegacyForm.Item>
      </Col>
      <Col xs={24} lg={11}>
        <LegacyForm.Item className={"tw:mb-0!"} name={"reasonCode"}>
          <Select
            placeholder={t("digitalAccount.samanCreate.selectReason")}
            allowClear
            className={"tw:w-full"}
            options={[
              {
                label: t("digitalAccount.samanCreate.depositOfCollateral"),
                value: BatchPaymentDetailsReasonCodeEnum.CCPA,
              },
              { label: t("digitalAccount.samanCreate.customsDuties"), value: BatchPaymentDetailsReasonCodeEnum.CDAP },
              {
                label: t("digitalAccount.samanCreate.cashAndCharityDonations"),
                value: BatchPaymentDetailsReasonCodeEnum.CPAC,
              },
              { label: t("digitalAccount.samanCreate.debtPayment"), value: BatchPaymentDetailsReasonCodeEnum.DRPA },
              { label: t("digitalAccount.samanCreate.otherMatters"), value: BatchPaymentDetailsReasonCodeEnum.FRTX },
              { label: t("digitalAccount.samanCreate.currencyAffairs"), value: BatchPaymentDetailsReasonCodeEnum.FXAP },
              {
                label: t("digitalAccount.samanCreate.buyingGoodsAndServices"),
                value: BatchPaymentDetailsReasonCodeEnum.GASP,
              },
              {
                label: t("digitalAccount.samanCreate.otherGovernmentServices"),
                value: BatchPaymentDetailsReasonCodeEnum.GEAC,
              },
              {
                label: t("digitalAccount.samanCreate.generalExpensesAndDailyAffairs"),
                value: BatchPaymentDetailsReasonCodeEnum.GPAC,
              },
              { label: t("digitalAccount.samanCreate.medicalAffairs"), value: BatchPaymentDetailsReasonCodeEnum.HIPA },
              {
                label: t("digitalAccount.samanCreate.realEstateTransactions"),
                value: BatchPaymentDetailsReasonCodeEnum.IMPT,
              },
              {
                label: t("digitalAccount.samanCreate.serviceInsuranceAffairs"),
                value: BatchPaymentDetailsReasonCodeEnum.IOSP,
              },
              {
                label: t("digitalAccount.samanCreate.investmentAndStockMarketAffairs"),
                value: BatchPaymentDetailsReasonCodeEnum.ISAP,
              },
              {
                label: t("digitalAccount.samanCreate.liquidityManagement"),
                value: BatchPaymentDetailsReasonCodeEnum.LMAP,
              },
              {
                label: t("digitalAccount.samanCreate.facilityRepayment"),
                value: BatchPaymentDetailsReasonCodeEnum.LRPA,
              },
              {
                label: t("digitalAccount.samanCreate.movablePropertyTransactions"),
                value: BatchPaymentDetailsReasonCodeEnum.MPTP,
              },
              { label: t("digitalAccount.samanCreate.salaryDeposit"), value: BatchPaymentDetailsReasonCodeEnum.POSA },
              {
                label: t("digitalAccount.samanCreate.retirementAffairs"),
                value: BatchPaymentDetailsReasonCodeEnum.RTAP,
              },
              { label: t("digitalAccount.samanCreate.taxSettlement"), value: BatchPaymentDetailsReasonCodeEnum.TCAP },
            ]}
          ></Select>
        </LegacyForm.Item>
      </Col>
      <Col xs={24} lg={11}>
        <LegacyForm.Item
          className={"tw:mb-0!"}
          name={"description"}
          rules={[
            {
              validator(_, value) {
                if (!isAlphabet(value, { withNumber: true }))
                  return Promise.reject(new Error(t("settlement.error.descriptionException")));
                if (value && value.length > 30) return Promise.reject(new Error(t("settlement.error.description")));
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder={t("digitalAccount.samanCreate.descriptionInput")} />
        </LegacyForm.Item>
      </Col>

      <Col xs={24} lg={2}>
        <Button block loading={loadingSubmit} type={"primary"} onClick={() => onSubmit()} data-google-track={ctaId}>
          {t("add.add")}
        </Button>
      </Col>
    </Row>
  );
};

export default ManualAdditionSaman;
